import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Eye, Link2Off } from "lucide-react";
import { FunnelStat } from "./helpers";

/**
 * Share-link funnel KPI card with per-claim drill-down table.
 * Rendered when at least one claim has a share-link issued.
 */
export function FunnelCard({ funnel, runShareScan, runningScan, lastRuns }) {
  if (!funnel || funnel.claims_with_shares <= 0) return null;
  return (
    <Card className="border-[#E5E7EB] mb-10" data-testid="funnel-card">
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <CardTitle className="font-['Outfit'] flex items-center gap-2">
            <Eye className="w-4 h-4 text-[#C25934]" />
            Share-link funnel
          </CardTitle>
          <div className="flex flex-wrap items-center gap-3">
            {funnel.revoked_total > 0 && (
              <span
                className="text-xs flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#F9F8F6] border border-[#E5E7EB] text-gray-700"
                data-testid="revoked-summary"
                title={`${funnel.revoked_auto} auto-revoked · ${funnel.revoked_manual} manual`}
              >
                <Link2Off className="w-3 h-3 text-[#C25934]" />
                {funnel.revoked_total} of {funnel.shares_issued_total} revoked
                {funnel.revoked_auto > 0 && (
                  <span className="text-gray-400">
                    ({funnel.revoked_auto} auto)
                  </span>
                )}
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              className="rounded-full border-[#1E3F33] text-[#1E3F33] hover:bg-[#1E3F33] hover:text-[#F9F8F6]"
              onClick={runShareScan}
              disabled={runningScan}
              data-testid="run-share-scan-button"
            >
              <Link2Off className="w-3.5 h-3.5 mr-2" />
              {runningScan ? "Scanning…" : "Run share scan now"}
            </Button>
            {lastRuns?.share_scan && (
              <span
                className="text-xs text-gray-500"
                data-testid="last-share-scan"
              >
                Last ran{" "}
                {new Date(lastRuns.share_scan.created_at).toLocaleString()}
              </span>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-4 gap-6 mb-6">
          <FunnelStat
            label="Open rate"
            value={`${funnel.open_rate_pct}%`}
            caption={`${funnel.claims_with_opens} of ${funnel.claims_with_shares} claims viewed`}
          />
          <FunnelStat
            label="Shares issued"
            value={funnel.shares_issued_total}
            caption={`${funnel.views_total} total views`}
          />
          <FunnelStat
            label="Avg share → first view"
            value={
              funnel.avg_hours_to_first_view != null
                ? `${funnel.avg_hours_to_first_view}h`
                : "—"
            }
            caption="time until the adjuster opens the packet"
          />
          <FunnelStat
            label="Avg share → approval"
            value={
              funnel.avg_hours_share_to_approval != null
                ? `${funnel.avg_hours_share_to_approval}h`
                : "—"
            }
            caption="share sent → carrier approval"
          />
        </div>
        {funnel.per_claim?.length > 0 && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Damage</TableHead>
                  <TableHead>Carrier</TableHead>
                  <TableHead className="text-right">Shares</TableHead>
                  <TableHead className="text-right">Views</TableHead>
                  <TableHead className="text-right">Time to open</TableHead>
                  <TableHead className="text-right">Time to approval</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {funnel.per_claim.slice(0, 8).map((r) => (
                  <TableRow
                    key={r.claim_id}
                    data-testid={`funnel-row-${r.claim_id}`}
                  >
                    <TableCell className="capitalize">
                      {r.damage_type}
                    </TableCell>
                    <TableCell>{r.carrier}</TableCell>
                    <TableCell className="text-right">
                      {r.shares_issued}
                    </TableCell>
                    <TableCell className="text-right">
                      <span
                        className={
                          r.views === 0
                            ? "text-gray-400"
                            : "text-[#1E3F33] font-medium"
                        }
                      >
                        {r.views}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {r.hours_to_first_view != null
                        ? `${r.hours_to_first_view}h`
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      {r.hours_share_to_approval != null
                        ? `${r.hours_share_to_approval}h`
                        : "—"}
                    </TableCell>
                    <TableCell className="capitalize">
                      {(r.status || "").replace("_", " ")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
